import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Send,
  Upload,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check } from "lucide-react";
import PiSymbol from "@/components/PiSymbol";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  "Fasteners",
  "Rubber",
  "Electronics",
  "Decals",
  "Injection Moulding",
  "Multiple/All",
];
const QUERY_TYPES = [
  "BOM Requirement",
  "New Vendor Evaluation",
  "FIDM Solution Enquiry",
  "Kanban/Kitting",
  "Consulting",
  "Other",
];
const OFFICES = [
  {
    city: "Mumbai",
    address: "IIM Mumbai Incubation Centre, Powai, Mumbai 400087",
    phone: "+91 22 4000 0000",
    email: "mumbai@piesolutions.in",
  },
  {
    city: "Roorkee",
    address: "IIT Roorkee Tech Partner Hub, Roorkee 247667",
    phone: "+91 1332 000 000",
    email: "roorkee@piesolutions.in",
  },
  {
    city: "Noida",
    address: "Sector 62, Noida 201309, Uttar Pradesh",
    phone: "+91 120 400 0000",
    email: "noida@piesolutions.in",
  },
];

const initial = {
  full_name: "",
  company_name: "",
  designation: "",
  email: "",
  phone: "",
  office: "",
  query_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [categories, setCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleCategory = (c) => {
    setCategories((arr) =>
      arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]
    );
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5 MB.");
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Valid phone required";
    if (!form.office) e.office = "Select an office";
    if (!form.query_type) e.query_type = "Select a query type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const fileToBase64 = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 =
          typeof result === "string" ? result.split(",")[1] : "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      let file_data_base64 = null;
      let file_name = null;
      if (file) {
        file_data_base64 = await fileToBase64(file);
        file_name = file.name;
      }
      const payload = {
        ...form,
        categories,
        file_data_base64,
        file_name,
        company_url: "", // honeypot — must stay empty
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Quote request received. We'll respond within 48 hours.");
      setSubmitted(true);
      setForm(initial);
      setCategories([]);
      setFile(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="overflow-x-hidden">
      {/* HEADER */}
      <section className="relative pt-32 pb-16 border-b border-white/5">
        <div className="absolute inset-0 grid-backdrop opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <PiSymbol size={42} />
            <span className="section-label">Contact</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05]">
            Start with your BOM. <br />
            <span className="text-gradient-green">End with one vendor.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-white/75 text-base md:text-lg leading-relaxed">
            Share your requirement and our team will respond within 48 hours
            with a consolidated quote across all verticals — no commitment
            required.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form
              data-testid="bom-contact-form"
              onSubmit={onSubmit}
              noValidate
              className="glass-card p-6 md:p-10 space-y-6"
            >
              {submitted && (
                <div
                  data-testid="contact-success-banner"
                  className="border border-[#39FF14]/40 bg-[#39FF14]/10 rounded-md p-4 flex items-start gap-3"
                >
                  <CheckCircle2 className="text-[#39FF14] mt-0.5" size={20} />
                  <div>
                    <div className="font-display font-semibold">
                      Submission received.
                    </div>
                    <div className="text-sm text-white/70">
                      We'll respond within 48 hours with a consolidated quote.
                    </div>
                  </div>
                </div>
              )}
              {/* Honeypot — invisible to humans, bots will fill it */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Company URL (leave blank)
                  <input
                    type="text"
                    name="company_url"
                    tabIndex={-1}
                    autoComplete="off"
                    onChange={() => {
                      /* honeypot — values are intentionally ignored on the client; server checks payload.company_url */
                    }}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name *" error={errors.full_name}>
                  <Input
                    data-testid="input-full-name"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Aarav Sharma"
                    className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                  />
                </Field>
                <Field label="Company Name *" error={errors.company_name}>
                  <Input
                    data-testid="input-company-name"
                    value={form.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    placeholder="ACME Manufacturing Pvt Ltd"
                    className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                  />
                </Field>
                <Field label="Designation">
                  <Input
                    data-testid="input-designation"
                    value={form.designation}
                    onChange={(e) => update("designation", e.target.value)}
                    placeholder="Procurement Manager"
                    className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                  />
                </Field>
                <Field label="Email *" error={errors.email}>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="aarav@acme.com"
                    className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                  />
                </Field>
                <Field label="Phone *" error={errors.phone}>
                  <Input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 98xxxxxxxx"
                    className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                  />
                </Field>
                <Field label="Pie Office to Connect *" error={errors.office}>
                  <Select
                    value={form.office}
                    onValueChange={(v) => update("office", v)}
                  >
                    <SelectTrigger
                      data-testid="select-office"
                      className="bg-[#0f0f0f] border-white/15 text-white focus:ring-[#39FF14]"
                    >
                      <SelectValue placeholder="Choose office" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f0f0f] border-white/15 text-white">
                      {["Mumbai", "Roorkee", "Noida"].map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div>
                <Label className="font-display uppercase tracking-[0.18em] text-xs text-white/70">
                  Category of Interest
                </Label>
                <div className="mt-3 flex flex-wrap gap-3">
                  {CATEGORIES.map((c) => {
                    const active = categories.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        data-testid={`chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleCategory(c)}
                        className={`px-4 py-2 rounded-full text-xs font-display uppercase tracking-[0.18em] border transition-all ${
                          active
                            ? "bg-[#39FF14] text-black border-[#39FF14]"
                            : "border-white/15 text-white/75 hover:border-[#39FF14]/60"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${
                              active
                                ? "bg-black border-black"
                                : "border-white/40"
                            }`}
                          >
                            {active && <Check size={10} className="text-[#39FF14]" strokeWidth={3} />}
                          </span>
                          {c}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Type of Query *" error={errors.query_type}>
                <Select
                  value={form.query_type}
                  onValueChange={(v) => update("query_type", v)}
                >
                  <SelectTrigger
                    data-testid="select-query-type"
                    className="bg-[#0f0f0f] border-white/15 text-white focus:ring-[#39FF14]"
                  >
                    <SelectValue placeholder="Choose query type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f0f] border-white/15 text-white">
                    {QUERY_TYPES.map((q) => (
                      <SelectItem key={q} value={q}>
                        {q}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Message / Requirement">
                <Textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  placeholder="Tell us about your BOM, volumes, target lead time and any specifications…"
                  className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14]"
                />
              </Field>

              <div>
                <Label className="font-display uppercase tracking-[0.18em] text-xs text-white/70">
                  Upload BOM or Part List (PDF / Excel, ≤ 5 MB)
                </Label>
                <div className="mt-3">
                  <label
                    data-testid="file-upload-label"
                    htmlFor="bom-upload"
                    className="flex items-center gap-3 border border-dashed border-white/20 hover:border-[#39FF14]/60 rounded-md p-5 cursor-pointer transition-colors"
                  >
                    <div className="w-10 h-10 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center">
                      <Upload size={18} className="text-[#39FF14]" />
                    </div>
                    <div className="flex-1">
                      {file ? (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm text-white/80 truncate">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            data-testid="file-remove"
                            onClick={(e) => {
                              e.preventDefault();
                              setFile(null);
                            }}
                            className="text-white/50 hover:text-[#DC143C]"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm text-white/85">
                            Drop your file here, or click to browse
                          </div>
                          <div className="text-xs text-white/50">
                            PDF, XLS, XLSX, CSV
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    id="bom-upload"
                    data-testid="file-upload-input"
                    type="file"
                    accept=".pdf,.xls,.xlsx,.csv"
                    onChange={onFile}
                    className="hidden"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="submit-contact-form"
                disabled={submitting}
                className="btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {submitting ? "Sending…" : "Get My Consolidated Quote →"}
              </button>
            </form>
          </div>

          <div className="space-y-4">
            {OFFICES.map((o) => (
              <div
                key={o.city}
                data-testid={`office-card-${o.city.toLowerCase()}`}
                className="glass-card p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Building2 size={18} className="text-[#39FF14]" />
                  <div className="font-display text-xl font-bold">
                    {o.city}
                  </div>
                </div>
                <div className="space-y-2 text-sm text-white/70">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 text-[#39FF14]" />
                    {o.address}
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-[#39FF14]" />
                    {o.phone}
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-[#39FF14]" />
                    {o.email}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-8">
            <span className="section-label">Associations</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {[
              "IIT Roorkee",
              "IIM Mumbai",
              "CIPET",
              "IGTR",
              "Coir Board, Govt. of India",
            ].map((p) => (
              <div
                key={p}
                className="font-display text-base md:text-lg uppercase tracking-[0.18em] text-white/40"
              >
                {p}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <PiSymbol size={48} />
            <div className="mt-4 font-display text-xl">
              Pie Solutions —{" "}
              <span className="text-[#39FF14]">For Future Factories</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="font-display uppercase tracking-[0.18em] text-xs text-white/70 mb-2 block">
        {label}
      </Label>
      {children}
      {error && (
        <div className="text-xs text-[#DC143C] mt-1.5 font-display tracking-wide">
          {error}
        </div>
      )}
    </div>
  );
}
